import { useEffect, useState } from 'react';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import intl from 'components/i18n/ReactIntlWrapper';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { handleAPIError } from 'common/errorHandler';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { ListOptionDTO, Persona } from 'axiosApi/models';
import { Chip, Box, Typography } from '@mui/material';
import { getUserEntity } from 'common/UserEntityProvider';
import { SCOPES } from 'common/permissions';
import PersonaBillingModal from './PersonaBillingModal';
import Spinner from 'components/Spinner';
import { useDialog } from 'common/DialogProvider';
import { DEFAULT_PAGINATION, Sort, useSearch } from 'hooks/useSearch';
import { DataGrid, GridColDef, GridInitialState, GridSortModel } from '@mui/x-data-grid';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

export default function BillingTable({
  entityId,
  idEntityType,
  billToDisplay,
  disabled,
  countries,
}: {
  entityId: number;
  idEntityType: number;
  billToDisplay?: boolean;
  disabled?: boolean;
  countries?: any[] | null;
}) {
  const [loading, setLoading] = useState(false);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const entityType = getUserEntity();
  const [editPersona, setEditPersona] = useState<Persona | null>(null);
  const [errors, setErrors] = useState({});
  const { invoiceApi }: Api = useApi();
  const toast = useToastMessageQueue();
  const [remainingPersonaTypes, setRemainingPersonaTypes] = useState<ListOptionDTO[]>([]);
  const [pagination, setPagination] = usePaginationPreference('billingPersonas', 10);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'Name', sort: 'asc' }]);

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const fetchBillingPersonas = async () => {
    setLoading(true);
    const { data: personaTypes } = await invoiceApi.apiVversionListPersonaTypeGet('1');
    const { data: personas } = await invoiceApi.apiVversionPersonaEntityIdEntityIdGet(
      entityId,
      '1',
      idEntityType,
      {}
    );
    setPersonas(personas.data?.['queryResult']);
    setRemainingPersonaTypes(
      personaTypes.data?.['queryResult'].filter((personaType: ListOptionDTO) => {
        return !personas.data?.['queryResult'].some((persona: Persona) => {
          return persona.idPersonaType === personaType.id;
        });
      })
    );
    setLoading(false);
  };

  useEffect(() => {
    fetchBillingPersonas();
  }, []);

  const editScopes = [SCOPES['employees.edit']];
  const deleteScopes = [SCOPES['employees.edit']];

  const { openDialog, closeDialog } = useDialog();

  const handleDelete = (id: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.personaRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          setLoading(true);
          try {
            const response = await invoiceApi.apiVversionPersonaIdDelete(id, '1', {});
            fetchBillingPersonas();
            setEditPersona(null);
            toast.success({ message: intl.get('personaRow.toast.success.handledelete') });
            closeDialog(dialogId);
          } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
            closeDialog(dialogId); // Close dialog even on error
          } finally {
            setLoading(false);
          }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => {
          closeDialog(dialogId);
        },
      },
    });
  };

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: DEFAULT_PAGINATION.pageNum,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
    },
    sorting: {
      sortModel: [
        {
          field: 'Name',
          sort: 'asc',
        },
      ],
    },
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: intl.get('personaCompact.table.thead.name'),
      width: 250,
      flex: 1,
    },
    {
      field: 'type',
      headerName: intl.get('personaCompact.table.thead.type'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{intl.get(params.row.personaType?.labelKey) || ''}</>,
    },
    {
      field: 'taxID',
      headerName: intl.get('personaCompact.table.thead.taxId'),
      width: 250,
      flex: 1,
    },
    {
      field: 'billTo',
      headerName: intl.get('personaCompact.table.thead.billTo'),
      width: 250,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.5} sx={{ height: '100%' }}>
          {params.row.invoicingEligibilities?.map((invoicingEligibility, index) => (
            <Chip
              key={index}
              label={invoicingEligibility?.personaTo?.name}
              variant="outlined"
              size="small"
              className="text-white bg-secondary me-1 border-white fw-semibold"
            />
          ))}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onEdit={() => {
              setEditPersona(params?.row);
              setErrors({});
            }}
            editScopes={[SCOPES['employees.edit']]}
            disabled={disabled || loading}
          />
          <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onDelete={() => handleDelete(params.row?.id)}
            deleteScopes={[SCOPES['employees.edit']]}
            disabled={disabled || loading}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <div className="card-header">
        <Typography variant="h6" color="primary" display={'flex'} alignItems="center" gap={1}>
          <MailOutlineIcon className="mb-1" /> {intl.get('personaCompact.header')}
        </Typography>
      </div>
      {loading ? (
        <Box
          style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Spinner small={true} />
        </Box>
      ) : (
        <div className="card-body">
          {personas && personas.length > 0 ? (
            <Box>
              <Box style={{ width: '100%', display: 'flex' }}>
                <DataGrid
                  rows={personas}
                  columns={columns}
                  loading={loading}
                  initialState={INITIAL_STATE}
                  rowCount={personas.length}
                  paginationModel={pagination}
                  onPaginationModelChange={setPagination}
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  pageSizeOptions={[5, 10, 20]}
                />
              </Box>
            </Box>
          ) : (
            <Box style={{ padding: '20px' }}></Box>
          )}

          <PersonaBillingModal
            entityId={entityId}
            idEntityType={idEntityType}
            editPersona={editPersona}
            onClose={() => {
              setEditPersona(null);
            }}
            onSave={async () => {
              await fetchBillingPersonas();
              setEditPersona(null);
              toast.success({ message: intl.get('persona.toast.success.handleSave') });
            }}
            editScopes={editScopes}
            deleteScopes={deleteScopes}
            remainingPersonaTypes={remainingPersonaTypes}
            billToDisplay={billToDisplay}
            disabled={disabled}
            countriesParent={countries}
          />
        </div>
      )}
    </Box>
  );
}
